import React from 'react'
import styled from 'styled-components'
import SimpleButtonControl from './simple-button-control'
import colours from '../lib/colours'
import IconControl from './icon-control'
import CloseButton from '../components/close-button'
import { MobileDeviceWidth } from '../lib/constants'

const StyledLegendContainer = styled.div`
  position: absolute;
  min-width: 100px;
  max-width: 90%;
  min-height: 40px;
  top: -20px;
  right: 15px;
  background-color: rgba(255, 255, 255, 1);
  z-index: 3;
  color: ${colours.white};
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.25);
  margin-top: 36px;
  max-height: calc(100% - 40px);
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const StyledLegendButton = styled.div`
  position: absolute;
  min-width: 100px;
  min-height: 40px;
  top: -20px;
  right: 15px;
  background-color: rgba(255, 255, 255, 1);
  z-index: 1;
  color: ${colours.white};
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.25);
  margin-top: 36px;
  @media (max-width: ${MobileDeviceWidth}px) {
    top: -26px;
    right: 10px;
  }
`

const StyledHeadingContainer = styled.div`
  text-align: left;
  padding: 10px 0 5px 20px;
  max-width: 342px;
  color: ${colours.darkPurple};
`

const StyledHeading = styled.h1`
  font-weight: 200;
  font-size: 20px;
  color: ${colours.darkPurple};
  line-height: 47px;
  margin: 0;
  float: left;
`

const StyledContainer = styled.div`
  text-align: left;
  padding-right: 24px;
  padding-left: 20px;
  padding-bottom: 20px;
  max-width: 342px;
  color: ${colours.darkPurple};
  overflow: auto;
`

const StyledCloseButtonContainer = styled.div`
  float: right;
`

const StyledLegendGroupHeading = styled.div`
  font-weight: 200;
  color: ${colours.mediumPurple};
  margin-bottom: 15px;
  margin-top: 15px;
`

const StyledLegendItemHeading = styled.div`
  font-weight: bold;
  font-size: 14px;
  display: flex;
  flex-direction: row;
`

const StyledLegendItemContainer = styled.div`
  margin-bottom: 5px;
`

const StyledSmallContainer = styled.div`
  text-align: center;
  padding: 0px;
`

const StyledLegendItemCheckboxContainer = styled.div`
  width: 18px;
  margin-right: 7px;
`

const StyledLegendItemCheckbox = styled.input`
  cursor: pointer;
  vertical-align: middle;
  width: 18px;
  height: 18px;
`

const StyledLegendItemLabel = styled.label`
  cursor: pointer;
  font-weight: 400;
  &:hover {
    color: ${colours.mediumPurple};
  }
`

const StyledSingleItemLegendItemLabel = styled(StyledLegendItemLabel)`
  margin-left: 7px;
`

const StyledMultiItemSwatchContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledSecondaryLegendItemLabel = styled.label`
  font-size: 12px;
  font-weight: 300;
`

const StyledSingleItemLegendSwatchContainer = styled.div`
  width: 20px;
`

const StyledMultiItemLegendSwatchContainer = styled.div`
  width: 20px;
  margin-left: 25px;
  margin-right: 8px;
`

const StyledLegendSwatch = styled.img`
  height: 20px;
  width: 20px;
`

interface IProps {
  legendItems: any[]
  toggleLayerVisibility: any
  mapScale: number
}

interface IState {
  open: boolean
}

class MapLegendOverlay extends React.Component<IProps, IState> {
  state = {
    open: false
  }

  handleClose = () => this.setState({ open: false })

  handleOpen = () => this.setState({ open: true })

  getLegendGroups() {
    return Array.from(new Set(this.props.legendItems.map(x => x.legendGroup)))
  }

  isItemVisibleAtScale(item: any): boolean {
    if (item.minScale === 0 && item.maxScale === 0) {
      return true
    }
    if (item.minScale !== 0 && item.minScale < this.props.mapScale) {
      return false
    }
    if (item.maxScale !== 0 && item.maxScale > this.props.mapScale) {
      return false
    }
    return true
  }

  renderItemLabel(item: any) {
    return this.isItemVisibleAtScale(item) ? (
      item.layerName
    ) : (
      <span title="Zoom in to see this layer">
        <IconControl icon="eye-slash" style={{ paddingRight: 5 }} />
        {item.layerName}
      </span>
    )
  }

  renderCheckBox(item: any) {
    return (
      <StyledLegendItemCheckboxContainer>
        <StyledLegendItemCheckbox
          type="checkbox"
          id={`chk${item.layerId}`}
          checked={item.visible}
          onChange={() => this.props.toggleLayerVisibility(item)}
        />
      </StyledLegendItemCheckboxContainer>
    )
  }

  renderSingleLegendItem(item: any) {
    return (
      <StyledLegendItemContainer key={item.layerId}>
        <StyledLegendItemHeading>
          {this.renderCheckBox(item)}
          <StyledSingleItemLegendSwatchContainer>
            {item.swatch ? <StyledLegendSwatch src={item.swatch} /> : null}
          </StyledSingleItemLegendSwatchContainer>
          <StyledSingleItemLegendItemLabel htmlFor={`chk${item.layerId}`}>
            {this.renderItemLabel(item)}
          </StyledSingleItemLegendItemLabel>
        </StyledLegendItemHeading>
      </StyledLegendItemContainer>
    )
  }

  renderMultiLegendItem(item: any) {
    return (
      <StyledLegendItemContainer key={item.layerId}>
        <StyledLegendItemHeading>
          {this.renderCheckBox(item)}
          <StyledLegendItemLabel htmlFor={`chk${item.layerId}`}>{this.renderItemLabel(item)}</StyledLegendItemLabel>
        </StyledLegendItemHeading>
        <div>
          {item.secondaryItems.map((secondaryItem: any) => {
            return (
              <StyledMultiItemSwatchContainer key={secondaryItem.label}>
                <StyledMultiItemLegendSwatchContainer>
                  {secondaryItem.swatch ? <StyledLegendSwatch src={secondaryItem.swatch} /> : null}
                </StyledMultiItemLegendSwatchContainer>
                <StyledSecondaryLegendItemLabel>{secondaryItem.label}</StyledSecondaryLegendItemLabel>
              </StyledMultiItemSwatchContainer>
            )
          })}
        </div>
      </StyledLegendItemContainer>
    )
  }

  renderExpanded() {
    return (
      <StyledLegendContainer>
        <StyledHeadingContainer>
          <StyledHeading>LAYERS</StyledHeading>
          <StyledCloseButtonContainer>
            <CloseButton onClick={this.handleClose} />
          </StyledCloseButtonContainer>
        </StyledHeadingContainer>
        <StyledContainer>
          {this.getLegendGroups().map(legendGroup => {
            return (
              <div key={legendGroup}>
                <StyledLegendGroupHeading>{legendGroup}</StyledLegendGroupHeading>
                {this.props.legendItems
                  .filter(item => item.legendGroup === legendGroup)
                  .sort((a, b) => a.legendOrder - b.legendOrder)
                  .map(item => {
                    return item.secondaryItems.length
                      ? this.renderMultiLegendItem(item)
                      : this.renderSingleLegendItem(item)
                  })}
              </div>
            )
          })}
        </StyledContainer>
      </StyledLegendContainer>
    )
  }

  renderClosed() {
    return (
      <StyledLegendButton>
        <StyledSmallContainer>
          <SimpleButtonControl onClick={this.handleOpen} text="Layers" />
        </StyledSmallContainer>
      </StyledLegendButton>
    )
  }

  render() {
    return this.state.open ? this.renderExpanded() : this.renderClosed()
  }
}

export default MapLegendOverlay
